import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { authFetch } from '../../utils/authFetch'
import { API_BASE_URL } from '../../config/api'
import { Palette, Building2, ExternalLink, Loader2 } from 'lucide-react'

interface Operator {
  id: string
  name: string
  subdomain: string
}

const BrandingManagement: React.FC = () => {
  const navigate = useNavigate()
  const [operators, setOperators] = useState<Operator[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchOperators = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await authFetch(`${API_BASE_URL}/api/super-admin/operators`)
        if (!response.ok) {
          throw new Error(`Failed to load operators (${response.status})`)
        }
        const data = await response.json()
        const list = data.operators ?? data
        setOperators(Array.isArray(list) ? list : [])
      } catch (err: any) {
        setError(err.message || 'Failed to load operators')
      } finally {
        setLoading(false)
      }
    }
    fetchOperators()
  }, [])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Palette className="h-6 w-6 text-blue-600 mr-2" />
          Branding Management
        </h1>
        <p className="text-gray-600 mt-1">Select an operator to manage their branding</p>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin mr-3" />
          <span className="text-gray-600">Loading operators...</span>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Operator list */}
      {!loading && !error && operators.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No operators found.
        </div>
      )}

      {!loading && !error && operators.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {operators.map((operator) => (
            <div
              key={operator.id}
              className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/super-admin/operators/${operator.id}`)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <Building2 className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{operator.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{operator.subdomain}</p>
                  </div>
                </div>
              </div>
              <button
                className="mt-4 w-full inline-flex items-center justify-center px-3 py-2 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/super-admin/operators/${operator.id}`)
                }}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Manage Branding
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default BrandingManagement
